/* eslint-disable import/no-default-export */
import { isTouch } from '@trello/browser';
import { dontUpsell } from '@trello/config';
import {
  PopoverMenu,
  PopoverMenuButton,
  PopoverMenuLink,
} from 'app/gamma/src/components/popover/popover-menu';
import { logout } from 'app/gamma/src/modules/state/models/session';
import { State } from 'app/gamma/src/modules/types';
import React from 'react';
import { connect } from 'react-redux';
import { hasMemberOrOrgAccount } from 'app/gamma/src/selectors/accounts';
import { getMe } from 'app/gamma/src/selectors/members';
import { MemberModel } from 'app/gamma/src/types/models';
import { Dispatch } from 'app/gamma/src/types';
import { HeaderTestIds } from '@trello/webdriverio-tests';
import { forTemplate } from 'app/common/lib/util/i18n';
const format = forTemplate('header_member_menu');

interface MemberMenuStateProps {
  canLogout: boolean;
  me: MemberModel | undefined;
  showBillingUrl: boolean;
}

interface MemberMenuDispatchProps {
  onClickLogout: () => void;
  onClickHelp: () => void;
  onClickShortcuts: () => void;
  onClickLanguagePicker: () => void;
  onClickChangePassword: () => void;
  onClickMenuLink: () => void;
}

interface MemberMenuProps
  extends MemberMenuStateProps,
    MemberMenuDispatchProps {}

const HeaderMemberMenu: React.FunctionComponent<MemberMenuProps> = ({
  canLogout,
  me,
  onClickLogout,
  showBillingUrl,
  onClickHelp,
  onClickShortcuts,
  onClickLanguagePicker,
  onClickChangePassword,
  onClickMenuLink,
}) => {
  if (!me) {
    return null;
  }

  return (
    <>
      <PopoverMenu>
        <>
          <PopoverMenuLink
            href={`/${me.username}/profile`}
            title={format('profile-and-visibility')}
            testId={HeaderTestIds.MemberMenuProfile}
            onClick={onClickMenuLink}
          />
          <PopoverMenuLink
            href={`/${me.username}/activity`}
            title={format('activity')}
            onClick={onClickMenuLink}
          />
          <PopoverMenuLink
            // this className is needed by Desktop for the My Cards MenuItem
            className={'js-cards'}
            href={`/${me.username}/cards`}
            title={format('cards')}
            testId={HeaderTestIds.MemberMenuCards}
            onClick={onClickMenuLink}
          />
          <PopoverMenuLink
            href={`/${me.username}/account`}
            title={format('settings')}
            appendSeparator={!showBillingUrl}
            testId={HeaderTestIds.MemberMenuSettings}
            onClick={onClickMenuLink}
          />
          {showBillingUrl && (
            <PopoverMenuLink
              href={`/${me.username}/billing`}
              title={format('billing')}
              appendSeparator
              onClick={onClickMenuLink}
            />
          )}
        </>
        <PopoverMenuButton
          appendSeparator={isTouch() && me.isAaMastered}
          title={format('help')}
          onClick={onClickHelp}
        />
        {!isTouch() && (
          <PopoverMenuButton
            title={format('shortcuts')}
            onClick={onClickShortcuts}
            appendSeparator={me.isAaMastered}
          />
        )}
        {!me.isAaMastered && (
          <PopoverMenuButton
            title={format('change-locale')}
            onClick={onClickLanguagePicker}
            appendSeparator
            testId={HeaderTestIds.MemberMenuLanguage}
          />
        )}
        {canLogout ? (
          <PopoverMenuButton
            title={format('log-out')}
            onClick={onClickLogout}
            testId={HeaderTestIds.MemberMenuLogout}
          />
        ) : (
          <PopoverMenuButton onClick={onClickChangePassword}>
            {format('log-out')}
            <br />
            {format('you-ll-need-to-set-a-password-first')}
          </PopoverMenuButton>
        )}
      </PopoverMenu>
    </>
  );
};

const mapStateToProps = (state: State): MemberMenuStateProps => {
  const me = getMe(state);

  return {
    canLogout: !!(me && me.loginTypes && me.loginTypes.length),
    me,
    showBillingUrl: hasMemberOrOrgAccount(state) && !dontUpsell,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    onClickLogout() {
      dispatch(logout());
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(HeaderMemberMenu);
